import { Building2, DollarSign, Calendar, Users, Trash2, Clock, TrendingUp } from 'lucide-react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { buildingService } from '@/services/tycoon/buildingService';
import type { OwnedBuilding, BuildingCosts } from '@/types/buildings';

interface BuildingsListProps {
  buildings: OwnedBuilding[];
  maintenanceCosts: BuildingCosts;
  onSellBuilding: (buildingId: string) => boolean;
  onUpgradeBuilding: (buildingId: string) => boolean;
  gameDate: { day: number; month: number; year: number };
  cash: number;
}

export function BuildingsList({ buildings, maintenanceCosts, onSellBuilding, onUpgradeBuilding, gameDate, cash }: BuildingsListProps) {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'headquarters': return 'amber';
      case 'office': return 'blue';
      case 'factory': return 'green';
      case 'lab': return 'purple';
      default: return 'gray';
    }
  };

  if (buildings.length === 0) {
    return (
      <div className="text-center py-12 text-theme-text-muted">
        No buildings owned yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {buildings.map(building => {
        const buildingType = buildingService.getBuildingType(building.buildingTypeId);
        if (!buildingType) return null;

        const maintenanceInfo = maintenanceCosts.byBuilding.find(b => b.buildingId === building.id);
        const color = getCategoryColor(buildingType.category);

        return (
          <div key={building.id} className="bg-theme-bg-content rounded-lg border p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`p-2 rounded-lg bg-${color}-100`}>
                  <Building2 className={`w-6 h-6 text-${color}-600`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-theme-text-primary">
                      {buildingType.name}
                    </h3>
                    <span className={`text-sm px-2 py-0.5 rounded-full bg-${color}-100 text-${color}-700 capitalize`}>
                      {buildingType.category}
                    </span>
                    {building.isUnderConstruction && (
                      <span className="text-sm px-2 py-0.5 rounded-full bg-theme-warning/20 text-theme-warning">
                        Under Construction
                      </span>
                    )}
                  </div>
                  
                  <p className="text-theme-text-secondary text-sm mb-3">{buildingType.description}</p>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-theme-text-muted" />
                      <span className="text-theme-text-secondary">Capacity:</span>
                      <span className="font-medium">
                        {buildingType.capacity.officeWorkers + buildingType.capacity.factoryWorkers + buildingType.capacity.technicalWorkers}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-theme-text-muted" />
                      <span className="text-theme-text-secondary">Maintenance:</span>
                      <span className="font-medium">{formatCurrency(maintenanceInfo?.maintenanceCost || 0)}/mo</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-theme-text-muted" />
                      <span className="text-theme-text-secondary">Purchased:</span>
                      <span className="font-medium">
                        {building.purchaseDate.month}/{building.purchaseDate.day}/{building.purchaseDate.year}
                      </span>
                    </div>
                  </div>

                  {/* Construction Progress */}
                  {building.isUnderConstruction && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-theme-warning" />
                          <span className="text-theme-text-secondary">Construction Progress</span>
                        </div>
                        <span className="text-sm font-medium text-theme-warning">
                          {(() => {
                            const { progress, daysRemaining } = buildingService.getConstructionProgress(building, gameDate);
                            return `${Math.round(progress)}% (${daysRemaining} days remaining)`;
                          })()}
                        </span>
                      </div>
                      <div className="w-full bg-theme-bg-tertiary rounded-full h-2">
                        <div
                          className="bg-theme-warning h-2 rounded-full transition-all"
                          style={{
                            width: `${buildingService.getConstructionProgress(building, gameDate).progress}%`
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Upgrade Progress */}
                  {building.isUpgrading && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm">
                          <TrendingUp className="w-4 h-4 text-theme-info" />
                          <span className="text-theme-text-secondary">Upgrade Progress</span>
                        </div>
                        <span className="text-sm font-medium text-theme-info">
                          {(() => {
                            const { progress, daysRemaining } = buildingService.getUpgradeProgress(building, gameDate);
                            const upgradeToBuildingType = buildingService.getBuildingType(building.upgradeToId!);
                            return `${Math.round(progress)}% to ${upgradeToBuildingType?.name || 'Unknown'} (${daysRemaining} days remaining)`;
                          })()}
                        </span>
                      </div>
                      <div className="w-full bg-theme-bg-tertiary rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${buildingService.getUpgradeProgress(building, gameDate).progress}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Action Buttons */}
              <div className="ml-4 flex items-center gap-2">
                {/* Upgrade Button */}
                {(() => {
                  const { canUpgrade, upgradeToId, cost, reason } = buildingService.canUpgradeBuilding(
                    building,
                    cash,
                    gameDate.year
                  );
                  const upgradeToBuildingType = upgradeToId ? buildingService.getBuildingType(upgradeToId) : null;
                  
                  return canUpgrade && upgradeToBuildingType ? (
                    <button
                      onClick={() => onUpgradeBuilding(building.id)}
                      className="p-2 text-theme-text-muted hover:text-theme-info hover:bg-theme-info/10 rounded-lg transition-colors"
                      title={`Upgrade to ${upgradeToBuildingType.name} for ${formatCurrency(cost || 0)}`}
                    >
                      <TrendingUp className="w-5 h-5" />
                    </button>
                  ) : (
                    buildingType.upgradesTo && (
                      <button
                        disabled
                        className="p-2 text-theme-text-muted bg-theme-bg-secondary rounded-lg cursor-not-allowed"
                        title={reason || 'Cannot upgrade'}
                      >
                        <TrendingUp className="w-5 h-5" />
                      </button>
                    )
                  );
                })()}

                {/* Sell Button */}
                {buildingType.canSell && (
                  <button
                    onClick={() => onSellBuilding(building.id)}
                    className="p-2 text-theme-text-muted hover:text-theme-error hover:bg-theme-error/10 rounded-lg transition-colors"
                    title={`Sell for ${formatCurrency(buildingType.sellValue || 0)}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}